// @ts-nocheck
/*
 * Port of views/dashboard/campaigns/detail.js (1887 lines). BYTE-IDENTICAL.
 * PrimeReact DataTable/Column swapped for obd3-gui DataTable + tanstack
 * ColumnDef; PrimeReact Button not used here (v1 uses react-bootstrap Button).
 * CKEditor import from v1 was unused in the file body — dropped to avoid
 * pulling a package v2 doesn't have. All 5 tab renderStep() branches preserved,
 * including case 4 (Call History) which is hidden via commented-out Nav.Item
 * in v1 but its handler + dummy data are kept exactly as v1 had them.
 * Recharts PieChart preserved for the Campaign Statistics donut.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import {
  API_AGENTS_LIST,
  API_VOICE_CAMPAIGNS_GET_ONE,
  API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP,
  API_PROMPT_ACTIVE_LIST,
  API_CAMPAIGN_REPORT,
} from '@/service/url';
import { getApiCall } from '@/service/httpCall';
import { useNavigate, useParams } from 'react-router-dom';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import { USER_ROLES } from '@/utilities/constant';
import Loader from '@/components/partials/components/Loader';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Storage } from '@/utilities/setting';
import { stripHtmlTags } from '@/utilities/helpers';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

const ScrollBox = ({ data }) => {
  if (!data) return '—';
  let parsedData = data;
  try {
    parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  } catch {}
  return (
    <div
      className="campaign-detail-scrollbox"
      style={{
        maxWidth: '280px',
        maxHeight: '130px',
        overflow: 'auto',
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        padding: '0.5rem',
        fontSize: '0.8rem',
      }}
    >
      <pre style={{ margin: 0 }}>
        {typeof parsedData === 'object' ? JSON.stringify(parsedData, null, 2) : parsedData}
      </pre>
    </div>
  );
};

const payloadBody = (rowData) => <ScrollBox data={rowData.payload} />;
const smsHeaderBody = (rowData) => <ScrollBox data={rowData.sms_webhook?.header} />;
const smsPayloadBody = (rowData) => <ScrollBox data={rowData.sms_webhook?.payload} />;
const webhookBody = (rowData) => <ScrollBox data={rowData.webhook?.body_json} />;
const webhookHeader = (rowData) => <ScrollBox data={rowData.webhook?.header_json} />;

const CampaignDetail = () => {
  const { id } = useParams();
  const [totalTalkSec, setTotalTalkSec] = useState(0);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [campaignName, setCampaignName] = useState('Test');
  const [campaignPrompts, setCampaignPrompts] = useState<any[]>([]);
  const [allPrompts, setAllPrompts] = useState<any[]>([]);
  const [filteredPrompts, setFilteredPrompts] = useState<any[]>([]);
  const [callGroupRows, setCallGroupRows] = useState<any[]>([]);
  const [webhookRows, setWebhookRows] = useState<any[]>([]);
  const [numbersRows, setNumbersRows] = useState<any[]>([]);
  const [contactMembers, setContactMembers] = useState<any[]>([]);
  const [scriptDetails, setScriptDetails] = useState<any[]>([]);
  const [scheduleRows, setScheduleRows] = useState<any[]>([]);
  const [groupRows, setGroupRows] = useState<any[]>([]);
  const [locationRows, setLocationRows] = useState<any[]>([]);
  const [agentRows, setAgentRows] = useState<any[]>([]);
  const [dispositionFormDataRows, setDispositionFormDataRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState<any>({
    name: '',
    contactGroup: '',
    agentAssignment: 'all',
    selectedQueue: '',
    selectedAgents: [],
    scriptOption: 'new',
    scriptTitle: '',
    scriptDescription: '',
    scriptShortDescription: '',
    did: '',
    timeBetweenCalls: '',
    maxRetries: '',
    startDate: '',
    endDate: '',
    weekDays: [],
    startHour: '',
    endHour: '',
  });
  const [agentsList, setAgentsList] = useState<any[]>([]);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [filteredAgents, setFilteredAgents] = useState<any[]>([]);
  const [isAgent, setIsAgent] = useState(false);
  const [isSuperamdin, setIsSuperadmin] = useState(false);
  const [liveAgents, setLiveAgents] = useState<any[]>([
    {
      id: 1,
      agent: 'Summer Sale',
      total_call: 5,
      answered: 2,
      total_talk_time: '01:00:00',
      status: 'active',
    },
    {
      id: 2,
      agent: 'Holiday Special',
      total_call: 5,
      answered: 2,
      total_talk_time: '01:00:00',
      status: 'active',
    },
  ]);

  const [contacts, setContacts] = useState<any[]>([
    { id: 1, name: 'Summer Sale', address: '987, ynr, hr, in', number: '9991066739', company: '01:00:00', action: 'active' },
    { id: 2, name: 'Winter Sale', address: '987, ynr, hr, in', number: '9350233907', company: '01:00:00', action: 'active' },
  ]);

  const [callHistory, setCallHistory] = useState<any[]>([
    { id: 1, date_time: '2025-01-03 13:39:01', agent: 'Agent 1', customer: '9991066739', disposition: 'Normal Clearing', call_duration: '01:00:00' },
    { id: 2, date_time: '2025-01-03 13:39:01', agent: 'Agent 2', customer: '9991066739', disposition: 'Not Clearing', call_duration: '01:00:00' },
  ]);

  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedDisposition, setSelectedDisposition] = useState('');

  const uniqueAgents = [...new Set(callHistory.map((item) => item.agent))];
  const uniqueDispositions = [...new Set(callHistory.map((item) => item.disposition))];
  const filteredCallHistoryData = useMemo(() => {
    return callHistory.filter((entry) => {
      return (
        (selectedAgent ? entry.agent === selectedAgent : true) &&
        (selectedDisposition ? entry.disposition === selectedDisposition : true)
      );
    });
  }, [callHistory, selectedAgent, selectedDisposition]);

  const [contactsData, setContactsData] = useState<any[]>([]);

  const filteredContacts = contactsData.filter((contact) =>
    Object.values(contact).join(' ').toLowerCase().includes(searchValue.toLowerCase()),
  );

  function onAgentsListSuccess(resp) { setAgentsList(resp); }
  function onCampaignDataSuccess(resp) {
    setCampaign(resp);
    setCampaignPrompts(resp.campaigns);
    setLoading(false);
  }
  function onPromptsDataSuccess(resp) { setAllPrompts(resp); }
  function onContactsSuccess(resp) {
    let data = resp;
    if (filteredAgents.length > 0 && isAgent == true) {
      let filteredData = data?.filter((item) => {
        return (
          item?.primary_number == filteredAgents[0]?.agent_number ||
          item?.alt1_number == filteredAgents[0]?.agent_number ||
          item?.alt2_number == filteredAgents[0]?.agent_number
        );
      });
      setContactsData(filteredData);
    } else {
      setContactsData(resp);
    }
  }

  useEffect(() => {
    getApiCall(API_AGENTS_LIST, { fields: 'min' }).then(onAgentsListSuccess).catch(onError);
    const url = API_VOICE_CAMPAIGNS_GET_ONE.replace(':id', id);
    getApiCall(url, {}).then(onCampaignDataSuccess).catch(onError);
    // talk time = sum of patch_duration across all legs, from the campaign report summary
    getApiCall(`${API_CAMPAIGN_REPORT.replace(':id', id)}?page=1&limit=1&include_summary=1`, {})
      .then((resp) => setTotalTalkSec(Number(resp?.summary?.total_talk_duration) || 0))
      .catch(() => {});
    const promptsUrl = `${API_PROMPT_ACTIVE_LIST}/all`;
    getApiCall(promptsUrl, {}).then(onPromptsDataSuccess).catch(onError);
  }, []);

  useEffect(() => {
    if (campaign?.voice_campaign_webhook_details?.length) {
      setWebhookRows(campaign.voice_campaign_webhook_details);
    } else setWebhookRows([]);

    if (campaign?.campaign_numbers && Array.isArray(campaign.campaign_numbers)) {
      setNumbersRows(campaign.campaign_numbers);
    } else setNumbersRows([]);

    if (campaign?.campaign_contact_group && Array.isArray(campaign.campaign_contact_group)) {
      const allMembers = campaign.campaign_contact_group.flatMap(
        (group) => group.voice_campaign_contact_group_details?.members || [],
      );
      setContactMembers(allMembers);
    } else setContactMembers([]);

    if (campaign?.voice_campaigns_scripts_details) {
      setScriptDetails([campaign?.voice_campaigns_scripts_details]);
    } else setScriptDetails([]);

    if (
      campaign?.voice_campaigns_call_group_details &&
      Array.isArray(campaign.voice_campaigns_call_group_details)
    ) {
      setCallGroupRows(campaign.voice_campaigns_call_group_details);
    } else setCallGroupRows([]);

    if (campaign?.voice_campaigns_schedule && Array.isArray(campaign.voice_campaigns_schedule)) {
      setScheduleRows(campaign.voice_campaigns_schedule);
    } else setScheduleRows([]);

    if (
      campaign?.voice_campaigns_call_group_details &&
      Array.isArray(campaign.voice_campaigns_call_group_details)
    ) {
      const extractedAgents = campaign.voice_campaigns_call_group_details.flatMap(
        (callGroup) =>
          callGroup.agent_group_campaign_details?.agent_schedule_mappings?.map(
            (mapping) => mapping.agent_vc,
          ) || [],
      );
      setAgentRows(extractedAgents);
    } else setAgentRows([]);

    setGroupRows(campaign?.campaignGroups || []);
    setLocationRows(campaign?.campaignLocations || []);

    if (campaign?.disposition_form_data && Array.isArray(campaign.disposition_form_data)) {
      setDispositionFormDataRows(campaign.disposition_form_data);
    } else setDispositionFormDataRows([]);
  }, [campaign]);

  const campaignGroupNameBody = (row) => row.voiceCampaignGroups?.group_name || '-';
  const campaignLocationNameBody = (row) =>
    row.voiceCampaignLocations?.location_name || row.location_name || '-';
  const weekDaysBody = (rowData) => (rowData.week_days ? rowData.week_days.join(', ') : '-');
  const groupNameBody = (rowData) => rowData.agent_group_campaign_details?.group_name || '-';
  const managerBody = (rowData) => rowData.agent_group_campaign_details?.manager || '-';
  const strategyBody = (rowData) => rowData.agent_group_campaign_details?.strategy || '-';
  const webhookTypeBody = (rowData) => (rowData.sms_webhook_id ? 'SMS Webhook' : 'Webhook');
  const fullNameBody = (rowData) => `${rowData.first_name} ${rowData.last_name}`;
  const webhookNameBody = (rowData) =>
    rowData.sms_webhook?.webhook_name || rowData.webhook?.webhook_name || '-';
  const webhookIdBody = (rowData) => rowData.sms_webhook?.id || rowData.webhook?.id || '-';
  const webhookUrlBody = (rowData) => rowData.sms_webhook?.url || rowData.webhook?.url || '-';
  const actualNumberBody = (rowData) => rowData.number_details_data?.actual_number || '-';
  const numberTypeBody = (rowData) => rowData.number_details_data?.number_type || '-';

  const getDispositionFormFromCampaign = (data) => {
    const candidates = [
      data?.dispositionForm,
      data?.disposition_form,
      data?.disposition_form_details,
      data?.voice_campaign_disposition_form_details,
      data?.voice_campaigns_disposition_form_details,
      data?.campaignDispositionForm,
    ];
    for (const candidate of candidates) {
      if (candidate && typeof candidate === 'object') return candidate;
    }
    return null;
  };

  const parseDispositionFields = (fields) => {
    if (!fields) return [];
    if (Array.isArray(fields)) return fields;
    if (typeof fields === 'string') {
      try {
        const parsed = JSON.parse(fields);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const dispositionForm = useMemo(() => getDispositionFormFromCampaign(campaign), [campaign]);
  const dispositionFormFields = useMemo(
    () => parseDispositionFields(dispositionForm?.fields),
    [dispositionForm],
  );

  const dispositionFormDataBody = (rowData) => <ScrollBox data={rowData?.data} />;
  const dispositionFormDataAgentBody = (rowData) =>
    rowData?.agent?.agent_name || rowData?.agent_id || '-';
  const dispositionFormDataAgentNumberBody = (rowData) => rowData?.agent?.agent_number || '-';
  const dispositionFormDataFormBody = (rowData) =>
    rowData?.disposition_form?.name ||
    rowData?.disposition_form?.id ||
    rowData?.disposition_form_id ||
    '-';

  useEffect(() => {
    if (allPrompts.length && campaignPrompts.length) {
      const usedPromptIds = campaignPrompts.map((item) => item.value);
      const matchedPrompts = allPrompts.filter((prompt) => usedPromptIds.includes(prompt.id));
      setFilteredPrompts(matchedPrompts);
    }
  }, [allPrompts, campaignPrompts]);

  useEffect(() => {
    if (campaign?.call_group) {
      const url = API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP.replace(':id', campaign.call_group);
      getApiCall(url, {}).then(onContactsSuccess, onError);
    }
  }, [campaign, isAgent, filteredAgents]);

  const fetchUserData = async () => {
    const rawUser = Storage.getItem('user');
    if (!rawUser) {
      console.error('User data not found in Storage');
      return;
    }
    let user;
    try {
      user = JSON.parse(rawUser);
    } catch (e) {
      console.error('Invalid JSON in Storage user:', e);
      return;
    }
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.role === USER_ROLES.CALL_CENTRE_AGENT) {
      setFilteredAgents([user.agentData]);
      setIsAgent(true);
    } else {
      setIsAgent(false);
      setFilteredAgents([]);
    }
  };

  useEffect(() => {
    if (campaign?.agents?.length > 0 && agentsList?.length > 0) {
      const selectedAgentIds = campaign.agents;
      const newFilteredAgents = agentsList.filter((agent) =>
        selectedAgentIds.includes(agent.id.toString()),
      );
      setFilteredAgents(newFilteredAgents);
    } else {
      fetchUserData();
    }
  }, [campaign, agentsList]);

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCampaign((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const nextStep = () => {
    if (!completedSteps.includes(step)) setCompletedSteps([...completedSteps, step]);
    setStep((prev) => prev + 1);
  };
  const prevStep = () => setStep((prev) => prev - 1);

  const actionBodyTemplate = (rowData) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleAction('call', rowData.id)}>
          <i className="pi pi-pencil mr-2" /> Call
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const handleAction = (action, id) => {
    console.log(`Action: ${action} on campaign ${id}`);
  };

  // Compact card style shared across the Overview tab. Uses theme
  // tokens so light/dark and the picked theme all cascade in.
  const cardStyle: any = {
    border: '1px solid hsl(var(--border) / 0.5)',
    borderRadius: '8px',
    padding: '12px 14px',
    background: 'hsl(var(--card))',
    flex: '1',
  };

  const statBoxStyle: any = { padding: '10px 16px', borderRadius: '6px', textAlign: 'center' };

  const legendBox = (color) =>
    ({
      display: 'inline-block',
      width: '16px',
      height: '10px',
      backgroundColor: color,
      borderRadius: '2px',
      marginRight: '4px',
    } as any);

  const doughnutTotal = Number(campaign?.total_nos) || 0;
  const doughnutConnected = Number(campaign?.connected_calls) || 0;
  const doughnutCompleted = Math.min(doughnutConnected, doughnutTotal);
  const doughnutRemaining = Math.max(0, doughnutTotal - doughnutConnected);

  const doughnutData = {
    labels: ['Completed', 'Remaining'],
    datasets: [
      { data: [doughnutCompleted, doughnutRemaining], backgroundColor: ['#2ebf3f', '#eb173b'], borderWidth: 0 },
    ],
  };

  const DOUGHNUT_COLORS_1 = ['#2ebf3f', '#eb173b'];

  const dataDoughnut = doughnutData.labels.map((label, index) => ({
    name: label,
    value: doughnutData.datasets[0].data[index],
  }));

  const calculateDropRate = () => {
    const totalNos = Number(campaign?.total_nos) || 0;
    const connectedNumbers = Number(campaign?.connected_calls) || 0;
    if (totalNos === 0) return 0;
    const dropRate = ((totalNos - connectedNumbers) / totalNos) * 100;
    return Math.round(dropRate * 100) / 100;
  };

  const calculateAvgConnection = () => {
    const totalNos = Number(campaign?.total_nos) || 0;
    const connectedNumbers = Number(campaign?.connected_calls) || 0;
    if (totalNos <= 0) return 0;
    const connectionRate = connectedNumbers / totalNos;
    return Math.round(connectionRate * 100 * 100) / 100;
  };

  const [playingId, setPlayingId] = useState<any>(null);
  const [invalidAudioIds, setInvalidAudioIds] = useState<any[]>([]);
  const audioRefs = useRef({} as any);

  const handleAudioError = (id) => {
    setInvalidAudioIds((prev) => [...prev, id]);
  };

  const handlePlayPause = (id) => {
    const current = audioRefs.current[id];
    if (playingId && playingId !== id) {
      audioRefs.current[playingId]?.pause();
      audioRefs.current[playingId].currentTime = 0;
    }
    if (playingId === id) {
      current.pause();
      setPlayingId(null);
    } else {
      current.play();
      setPlayingId(id);
    }
  };

  const handleLoadedMetadata = (id) => {
    const duration = audioRefs.current[id]?.duration || 0;
  };

  if (loading) return <Loader />;

  const numbersColumns: ColumnDef<any>[] = [
    { accessorKey: 'cli', header: 'ID' },
    { id: 'actual_number', header: 'Actual Number', cell: ({ row }) => actualNumberBody(row.original) },
    { id: 'number_type', header: 'Number Type', cell: ({ row }) => numberTypeBody(row.original) },
  ];

  const scriptColumns: ColumnDef<any>[] = [
    { accessorKey: 'id', header: 'ID' },
    { accessorKey: 'title', header: 'Title' },
    {
      accessorKey: 'short_description',
      header: 'Short Description',
      cell: ({ row }) => stripHtmlTags(row.original.short_description) || '—',
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => stripHtmlTags(row.original.description) || '—',
    },
  ];

  const dispositionFieldsColumns: ColumnDef<any>[] = [
    { accessorKey: 'field_name', header: 'Field Name' },
    { accessorKey: 'field_type', header: 'Type' },
    { id: 'required', header: 'Required', cell: ({ row }) => (row.original?.is_required ? 'Yes' : 'No') },
    {
      id: 'attach_to_cdr',
      header: 'Attach to CDR',
      cell: ({ row }) => (row.original?.attach_to_cdr ? 'Yes' : 'No'),
    },
    {
      id: 'default',
      header: 'Default',
      cell: ({ row }) =>
        row.original?.field_default === null ||
        row.original?.field_default === undefined ||
        row.original?.field_default === ''
          ? '-'
          : String(row.original.field_default),
    },
    {
      id: 'min',
      header: 'Min',
      cell: ({ row }) =>
        row.original?.field_min === null ||
        row.original?.field_min === undefined ||
        row.original?.field_min === ''
          ? '-'
          : String(row.original.field_min),
    },
    {
      id: 'max',
      header: 'Max',
      cell: ({ row }) =>
        row.original?.field_max === null ||
        row.original?.field_max === undefined ||
        row.original?.field_max === ''
          ? '-'
          : String(row.original.field_max),
    },
  ];

  const dispositionFormDataColumns: ColumnDef<any>[] = [
    { id: 'form', header: 'Form', cell: ({ row }) => dispositionFormDataFormBody(row.original) },
    { id: 'agent', header: 'Agent', cell: ({ row }) => dispositionFormDataAgentBody(row.original) },
    {
      id: 'agent_number',
      header: 'Agent Number',
      cell: ({ row }) => dispositionFormDataAgentNumberBody(row.original),
    },
    { id: 'date', header: 'Date', cell: ({ row }) => formatDateTime(row.original?.created_at) },
    { id: 'data', header: 'Data', cell: ({ row }) => dispositionFormDataBody(row.original) },
  ];

  const groupColumns: ColumnDef<any>[] = [
    { accessorKey: 'group_id', header: 'Dialer Group ID' },
    { id: 'group_name', header: 'Dialer Group Name', cell: ({ row }) => campaignGroupNameBody(row.original) },
  ];

  const locationColumns: ColumnDef<any>[] = [
    { accessorKey: 'location_id', header: 'Dialer Location ID' },
    { id: 'location_name', header: 'Dialer Location Name', cell: ({ row }) => campaignLocationNameBody(row.original) },
  ];

  const callGroupColumns: ColumnDef<any>[] = [
    { accessorKey: 'call_group_id', header: 'Call Group ID' },
    { id: 'dialer_group_name', header: 'Dialer Group Name', cell: ({ row }) => groupNameBody(row.original) },
    { id: 'dialer_group_owner', header: 'Dialer Group Owner', cell: ({ row }) => managerBody(row.original) },
    { id: 'strategy', header: 'Strategy', cell: ({ row }) => strategyBody(row.original) },
    { accessorKey: 'dtmf', header: 'DTMF' },
  ];

  const scheduleColumns: ColumnDef<any>[] = [
    { accessorKey: 'start_time', header: 'Start Time' },
    { accessorKey: 'end_time', header: 'End Time' },
    { id: 'week_days', header: 'Week Days', cell: ({ row }) => weekDaysBody(row.original) },
  ];

  const agentColumns: ColumnDef<any>[] = [
    { accessorKey: 'agent_name', header: 'Agent' },
    { accessorKey: 'agent_number', header: 'Agent Number' },
    { accessorKey: 'email_id', header: 'Agent Email' },
    {
      accessorKey: 'login_status',
      header: 'Status',
      cell: ({ row }) => (row.original?.login_status == 0 ? 'Inactive' : 'Active'),
    },
  ];

  const contactColumns: ColumnDef<any>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => fullNameBody(row.original) },
    { accessorKey: 'primary_number', header: 'Primary Number' },
    { accessorKey: 'alt1_number', header: 'Alt 1 Number' },
    { accessorKey: 'alt2_number', header: 'Alt 2 Number' },
    { accessorKey: 'company', header: 'Company' },
    { accessorKey: 'city', header: 'City' },
  ];

  const callHistoryColumns: ColumnDef<any>[] = [
    { accessorKey: 'date_time', header: 'Date/Time' },
    { accessorKey: 'agent', header: 'Agent' },
    { accessorKey: 'customer', header: 'Customer' },
    { accessorKey: 'disposition', header: 'Disposition' },
    { accessorKey: 'call_duration', header: 'Call Duration' },
  ];

  const webhookColumns: ColumnDef<any>[] = [
    { id: 'wh_id', header: 'ID', cell: ({ row }) => webhookIdBody(row.original) },
    { id: 'wh_type', header: 'Type', cell: ({ row }) => webhookTypeBody(row.original) },
    { id: 'wh_name', header: 'Name', cell: ({ row }) => webhookNameBody(row.original) },
    { accessorKey: 'event', header: 'Event' },
    { accessorKey: 'duration', header: 'Duration' },
    { id: 'wh_url', header: 'URL', cell: ({ row }) => webhookUrlBody(row.original) },
    { accessorKey: 'req_type', header: 'Request Type' },
    { id: 'payload', header: 'Payload', cell: ({ row }) => payloadBody(row.original) },
    { id: 'sms_header', header: 'SMS Header', cell: ({ row }) => smsHeaderBody(row.original) },
    { id: 'sms_payload', header: 'SMS Payload', cell: ({ row }) => smsPayloadBody(row.original) },
    { id: 'wh_body', header: 'Webhook Body', cell: ({ row }) => webhookBody(row.original) },
    { id: 'wh_header', header: 'Webhook Header', cell: ({ row }) => webhookHeader(row.original) },
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="cdp-overview">
            {campaign?.campaign_type && campaign?.campaign_type != '1' && campaign?.campaign_type != '3' && (
              <div className="campaign-detail-card" style={cardStyle}>
                <p><strong>Template Type</strong></p>
                <p>
                  {{ 0: 'Simple IVR', 1: 'DTMF', 2: 'Call Patch', 3: 'Custom IVR' }[campaign?.template_id] ||
                    'Unknown'}
                </p>
              </div>
            )}
            <div className="campaign-detail-card" style={{ ...cardStyle, marginBottom: '20px', marginTop: '20px' }}>
              <p><strong>DID(s)</strong></p>
              <div style={{ overflowX: 'auto' }}>
                <DataTable
                  tableId="campaign-detail.numbers"
                  columns={numbersColumns}
                  data={numbersRows}
                  pageSize={10}
                  emptyTitle="No numbers"
                  disableExport
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', marginTop: '20px' }}>
              <div className="campaign-detail-card" style={cardStyle}>
                <p><strong>Call Center</strong></p>
                <p>{campaign?.campaignCallCenter?.name || '-'}</p>
              </div>
              <div className="campaign-detail-card" style={cardStyle}>
                <p><strong>Company</strong></p>
                <p>{campaign?.campaignCompany?.name || '-'}</p>
              </div>
            </div>
            {scriptDetails?.length > 0 && (
              <div className="campaign-detail-card" style={{ ...cardStyle, marginBottom: '20px', marginTop: '20px' }}>
                <p><strong>Script Used</strong></p>
                <div style={{ overflowX: 'auto' }} className="broad-scrollbar">
                  <DataTable
                    tableId="campaign-detail.script"
                    columns={scriptColumns}
                    data={scriptDetails}
                    pageSize={10}
                    emptyTitle="No script"
                    disableExport
                  />
                </div>
                <style>{`.broad-scrollbar::-webkit-scrollbar { height: 8px; }`}</style>
              </div>
            )}
            <div className="campaign-detail-card" style={{ ...cardStyle, marginBottom: '20px', marginTop: '20px' }}>
              <p style={{ marginBottom: '30px' }}><strong>Disposition Form Used</strong></p>
              {dispositionForm ? (
                <>
                  <div style={{ display: 'flex', gap: '80px', flexWrap: 'wrap' }}>
                    <div>
                      <p style={{ marginBottom: 4, fontSize: '13px' }}><strong>Name</strong></p>
                      <p style={{ marginBottom: 0 }}>{dispositionForm?.name || '-'}</p>
                    </div>
                    <div>
                      <p style={{ marginBottom: 4, fontSize: '13px' }}><strong>Creation Date</strong></p>
                      <p style={{ marginBottom: 0 }}>
                        {formatDateTime(dispositionForm?.created_at || dispositionForm?.createdAt)}
                      </p>
                    </div>
                    <div>
                      <p style={{ marginBottom: 4, fontSize: '13px' }}><strong>Fields</strong></p>
                      <p style={{ marginBottom: 0 }}>
                        {Array.isArray(dispositionFormFields) ? dispositionFormFields.length : 0}
                      </p>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto', marginTop: '12px' }} className="broad-scrollbar">
                    <DataTable
                      tableId="campaign-detail.disposition-fields"
                      columns={dispositionFieldsColumns}
                      data={dispositionFormFields}
                      pageSize={10}
                      emptyTitle="No disposition fields found"
                      disableExport
                    />
                  </div>
                </>
              ) : (
                <p style={{ marginBottom: 0 }}>
                  No disposition form selected. Agents will see default form only.
                </p>
              )}
            </div>
            <div className="campaign-detail-card" style={{ ...cardStyle, marginBottom: '20px', marginTop: '20px' }}>
              <p><strong>Disposition Form Data</strong></p>
              <div style={{ overflowX: 'auto' }} className="broad-scrollbar">
                <DataTable
                  tableId="campaign-detail.disposition-data"
                  columns={dispositionFormDataColumns}
                  data={dispositionFormDataRows}
                  pageSize={10}
                  emptyTitle="No disposition form data found"
                  disableExport
                />
              </div>
            </div>
            {filteredPrompts?.length > 0 && (
              <div className="campaign-detail-card campaign-detail-prompts" style={cardStyle}>
                <p><strong>Used Prompts</strong></p>
                <table className="cdp-mini-table">
                  <thead>
                    <tr>
                      <th>Prompt Name</th>
                      <th>Prompt ID</th>
                      <th>Category</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPrompts?.map((item) => (
                      <tr key={item.id}>
                        <td>{item.prompt_name}</td>
                        <td>{item.id}</td>
                        <td>{item.prompt_category}</td>
                        <td>
                          <audio
                            ref={(el) => (audioRefs.current[item.id] = el)}
                            src={
                              Array.isArray(item.prompt_url)
                                ? item.prompt_url[0]?.url || ''
                                : item.prompt_url || ''
                            }
                            onLoadedMetadata={() => handleLoadedMetadata(item.id)}
                            onEnded={() => setPlayingId(null)}
                            onError={() => handleAudioError(item.id)}
                          />
                          <button
                            onClick={() => {
                              if (!invalidAudioIds.includes(item.id)) handlePlayPause(item.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: invalidAudioIds.includes(item.id) ? 'not-allowed' : 'pointer',
                              opacity: invalidAudioIds.includes(item.id) ? 0.5 : 1,
                            }}
                            title={
                              invalidAudioIds.includes(item.id)
                                ? 'Audio not supported'
                                : playingId === item.id
                                  ? 'Pause'
                                  : 'Play'
                            }
                            disabled={invalidAudioIds.includes(item.id)}
                          >
                            {playingId === item.id ? (
                              <svg width="20" height="20" viewBox="0 0 24 24">
                                <path d="M6 5h4v14H6zM14 5h4v14h-4z" fill="currentColor" />
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" fill="currentColor" />
                              </svg>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {groupRows?.length > 0 && (
              <div className="campaign-detail-card" style={{ ...cardStyle, marginBottom: '20px', marginTop: '20px' }}>
                <p><strong>Campaign Dialer Group</strong></p>
                <div>
                  <DataTable
                    tableId="campaign-detail.groups"
                    columns={groupColumns}
                    data={groupRows}
                    pageSize={10}
                    emptyTitle="No groups"
                    disableExport
                  />
                </div>
              </div>
            )}
            {locationRows?.length > 0 && (
              <div className="campaign-detail-card" style={{ ...cardStyle, marginBottom: '20px', marginTop: '20px' }}>
                <p><strong>Campaign Dialer Location</strong></p>
                <div>
                  <DataTable
                    tableId="campaign-detail.locations"
                    columns={locationColumns}
                    data={locationRows}
                    pageSize={10}
                    emptyTitle="No locations"
                    disableExport
                  />
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', marginTop: '20px' }}>
              {campaign?.template_id == '1' && (
                <div style={{ ...cardStyle, flex: 'none', width: '170px', height: '80px' }}>
                  <p style={{ marginTop: '-5px' }}><strong>DTMF</strong></p>
                  <p style={{ marginTop: '-10px' }}>{campaign?.dtmf || '-'}</p>
                </div>
              )}
              {campaign?.campaign_type &&
                campaign?.campaign_type != '1' &&
                campaign?.campaign_type != '3' &&
                campaign?.menu_wait_time && (
                  <div style={{ ...cardStyle, flex: 'none', width: '170px', height: '80px' }}>
                    <p style={{ marginTop: '-5px' }}><strong>Menu Wait Time</strong></p>
                    <p style={{ marginTop: '-10px' }}>{campaign?.menu_wait_time}</p>
                  </div>
                )}
              {campaign?.campaign_type &&
                campaign?.campaign_type != '1' &&
                campaign?.campaign_type != '3' &&
                campaign?.re_prompt && (
                  <div style={{ ...cardStyle, flex: 'none', width: '170px', height: '80px' }}>
                    <p style={{ marginTop: '-5px' }}><strong>Re Prompt</strong></p>
                    <p style={{ marginTop: '-10px' }}>{campaign?.re_prompt}</p>
                  </div>
                )}
            </div>
            {callGroupRows?.length > 0 && (
              <div style={{ ...cardStyle, marginBottom: '20px', marginTop: '20px' }}>
                <p><strong>Call Group Data</strong></p>
                <div style={{ overflowX: 'auto' }}>
                  <DataTable
                    tableId="campaign-detail.call-groups"
                    columns={callGroupColumns}
                    data={callGroupRows}
                    pageSize={10}
                    emptyTitle="No call groups"
                    disableExport
                  />
                </div>
              </div>
            )}
            {scheduleRows?.length > 0 && (
              <div style={{ ...cardStyle, marginBottom: '20px', marginTop: '20px' }}>
                <p><strong>Schedule Details</strong></p>
                <div style={{ overflowX: 'auto' }}>
                  <DataTable
                    tableId="campaign-detail.schedule"
                    columns={scheduleColumns}
                    data={scheduleRows}
                    pageSize={10}
                    emptyTitle="No schedule"
                    disableExport
                  />
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', marginTop: '20px' }}>
              <div className="campaign-detail-card" style={cardStyle}>
                <p><strong>Total Talk-time</strong></p>
                <p>{totalTalkSec}s</p>
              </div>
              <div className="campaign-detail-card" style={cardStyle}>
                <p><strong>Drop Rate</strong></p>
                <p>{calculateDropRate()}%</p>
              </div>
              <div className="campaign-detail-card" style={cardStyle}>
                <p><strong>Average Connection</strong></p>
                <p>{calculateAvgConnection()}%</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', marginTop: '20px' }}>
              <div className="campaign-detail-card" style={cardStyle}>
                <p><strong>Max Retry Count</strong></p>
                <p>{campaign?.max_retries || '0'}</p>
              </div>
              <div className="campaign-detail-card" style={cardStyle}>
                <p><strong>Retry Interval</strong></p>
                <p>{campaign?.retry_interval || '0'}s</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <div className="campaign-detail-card" style={{ ...cardStyle, flex: 1 }}>
                <p><strong>Campaign statistics</strong></p>
                <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                  <div><span style={legendBox('#2ebf3f')} /> Completed</div>
                  <div><span style={legendBox('#eb173b')} /> Remaining</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                  <div style={{ width: '120px', height: '120px' }}>
                    <ResponsiveContainer width="100%" height={120}>
                      <PieChart>
                        <Pie
                          data={dataDoughnut}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={50}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                        >
                          {dataDoughnut.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={DOUGHNUT_COLORS_1[index % DOUGHNUT_COLORS_1.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', fontWeight: 'bold' }}>
                  <div>
                    <p>{(Number(campaign?.nos_processed) || 0) + (Number(campaign?.auto_retry_count) || 0)}</p>
                    <p style={{ fontWeight: 'normal' }}>Total Calls Dialed</p>
                  </div>
                  <div>
                    <p>{campaign?.connected_calls}</p>
                    <p style={{ fontWeight: 'normal' }}>Connected</p>
                  </div>
                  <div>
                    <p>
                      {(Number(campaign?.total_nos) || 0) -
                        (Number(campaign?.dnd_count) || 0) -
                        (Number(campaign?.nos_processed) || 0)}
                    </p>
                    <p style={{ fontWeight: 'normal' }}>Pending</p>
                  </div>
                </div>
              </div>
              <div className="campaign-detail-card" style={{ ...cardStyle, flex: 1 }}>
                <p><strong>Call statistics</strong></p>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '20px' }}>
                  {[
                    { label: 'Total calls', value: (Number(campaign?.nos_processed) || 0) + (Number(campaign?.auto_retry_count) || 0) },
                    { label: 'Answered', value: Number(campaign?.connected_calls) || 0 },
                    { label: 'Failed', value: Math.max(0, ((Number(campaign?.nos_processed) || 0) + (Number(campaign?.auto_retry_count) || 0)) - (Number(campaign?.connected_calls) || 0)) },
                  ].map((item, idx) => (
                    <div key={idx} className="campaign-detail-statbox" style={statBoxStyle}>
                      <p style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{item.value}</p>
                      <p style={{ fontSize: '0.9rem' }}>{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Compact vertical rhythm + themed native table styling
                scoped to the Overview tab. `!important` beats the
                inline marginTop/marginBottom the individual cards
                still emit (kept there so any other consumer of the
                cardStyle object stays unchanged). */}
            <style>{`
              .cdp-overview .campaign-detail-card {
                margin: 10px 0 !important;
                padding: 12px 14px !important;
              }
              .cdp-overview .campaign-detail-card p {
                margin: 0 0 6px !important;
              }
              .cdp-overview .campaign-detail-card p:last-child {
                margin-bottom: 0 !important;
              }
              .cdp-overview > div[style*="display: flex"] {
                margin: 10px 0 !important;
              }
              .cdp-mini-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 13px;
              }
              .cdp-mini-table thead th {
                text-align: left;
                padding: 8px 10px;
                background: hsl(var(--primary) / 0.06);
                border-bottom: 1px solid hsl(var(--border) / 0.6);
                font-size: 11px;
                font-weight: 700;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                color: hsl(var(--muted-foreground));
              }
              .cdp-mini-table thead th:first-child { border-top-left-radius: 6px; }
              .cdp-mini-table thead th:last-child { border-top-right-radius: 6px; }
              .cdp-mini-table tbody td {
                padding: 8px 10px;
                border-bottom: 1px solid hsl(var(--border) / 0.4);
                color: hsl(var(--foreground));
              }
              .cdp-mini-table tbody tr:last-child td { border-bottom: 0; }
              .cdp-mini-table tbody tr:hover td { background: hsl(var(--primary) / 0.04); }
            `}</style>
          </div>
        );
      case 2:
        return (
          <div className="pb-4">
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                tableId="campaign-detail.agents"
                columns={agentColumns}
                data={agentRows}
                pageSize={10}
                emptyTitle="No agents"
                disableExport
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="pb-4">
            <div className="d-flex align-items-center flex-wrap">
              <Input
                placeholder="Search Contacts"
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{ maxWidth: '250px', marginBottom: '10px' }}
              />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                tableId="campaign-detail.contacts"
                columns={contactColumns}
                data={contactMembers}
                pageSize={10}
                emptyTitle="No contacts"
                disableExport
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="pb-4">
            <div className="d-flex align-items-center flex-wrap gap-3 mb-3">
              <select
                className="form-control form-select"
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                style={{ maxWidth: '200px' }}
              >
                <option value="">All Agents</option>
                {uniqueAgents.map((agent, idx) => (
                  <option key={idx} value={agent}>
                    {agent}
                  </option>
                ))}
              </select>
              <select
                className="form-control form-select"
                value={selectedDisposition}
                onChange={(e) => setSelectedDisposition(e.target.value)}
                style={{ maxWidth: '200px' }}
              >
                <option value="">All Dispositions</option>
                {uniqueDispositions.map((dispo, idx) => (
                  <option key={idx} value={dispo}>
                    {dispo}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                tableId="campaign-detail.call-history"
                columns={callHistoryColumns}
                data={filteredCallHistoryData}
                pageSize={10}
                emptyTitle="No call history"
                disableExport
              />
            </div>
          </div>
        );
      case 5:
        return (
          <div className="pb-4">
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                tableId="campaign-detail.webhooks"
                columns={webhookColumns}
                data={webhookRows}
                pageSize={15}
                emptyTitle="No webhooks"
                disableExport
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Tone-tinted pill matching Live Campaigns / Campaigns Summary.
  const statusBodyTemplate = (camp_status) => {
    camp_status = Number(camp_status);
    const statusMap: Record<number, { label: string; tone: string; live?: boolean }> = {
      0:  { label: 'Ready',           tone: 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300' },
      1:  { label: 'Running',         tone: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300', live: true },
      2:  { label: 'Completed',       tone: 'bg-indigo-100 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300' },
      3:  { label: 'Low Balance',     tone: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300' },
      4:  { label: 'Error',           tone: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300' },
      5:  { label: 'Error',           tone: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300' },
      6:  { label: 'Cancelling',      tone: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300' },
      7:  { label: 'Cancelled',       tone: 'bg-pink-100 text-pink-700 ring-pink-200 dark:bg-pink-950/40 dark:text-pink-300' },
      8:  { label: 'Pausing',         tone: 'bg-cyan-100 text-cyan-700 ring-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300' },
      9:  { label: 'Paused',          tone: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300' },
      10: { label: 'Resuming',        tone: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300', live: true },
      21: { label: 'DND Processing',  tone: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300', live: true },
      22: { label: 'Waiting for Ch.', tone: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300', live: true },
    };
    const status = statusMap[camp_status];
    if (!status) return null;
    return (
      <span
        className={
          'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
          status.tone
        }
      >
        {status.label}
      </span>
    );
  };

  const navLinkStyle = (idx) => {
    const isActive = step === idx;
    const isCompleted = completedSteps.includes(idx) && !isActive;

    let color = 'hsl(var(--muted-foreground))';
    let background = 'transparent';
    let borderBottom = '2px solid transparent';

    if (isActive) {
      color = 'hsl(var(--primary))';
      background = 'hsl(var(--primary) / 0.10)';
      borderBottom = '2px solid hsl(var(--primary))';
    } else if (isCompleted) {
      color = 'rgb(4, 120, 87)'; // emerald-700
      background = 'rgba(16, 185, 129, 0.08)';
      borderBottom = '2px solid rgba(16, 185, 129, 0.35)';
    }

    return {
      color,
      background,
      borderBottom,
      fontWeight: isActive ? 600 : 500,
      padding: '0.65rem 1rem',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
      border: '1px solid transparent',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
      position: 'relative',
      zIndex: 2,
      boxShadow: 'none',
      cursor: 'pointer',
    } as any;
  };

  return (
    <FillPage>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span>Campaign Detail</span>
            {campaign?.campaign_name && (
              <span className="text-base font-normal text-muted-foreground">
                / <span className="font-semibold text-foreground">{campaign.campaign_name}</span>
              </span>
            )}
            {campaign?.campaign_status !== undefined && campaign?.campaign_status !== null && (
              <span>{statusBodyTemplate(campaign?.campaign_status)}</span>
            )}
          </span>
        }
        description="Snapshot of this campaign — contacts, timings, and reports."
        actions={
          <Button variant="outline" onClick={() => navigate('/campaigns')}>
            <FaArrowLeft style={{ fontSize: '0.85rem' }} />
            Back
          </Button>
        }
      />
      <Card className="border border-border/60 bg-card">
        <CardHeader
          className="p-0"
          style={{
            borderRadius: '8px 8px 0 0',
            borderBottom: '1px solid hsl(var(--border) / 0.5)',
          }}
        >
          <div role="tablist" className="flex flex-wrap gap-1 px-3 pt-2">
            {[
              { key: 1, label: 'Overview' },
              ...(campaign?.template_id !== '1' ? [{ key: 2, label: 'Agents' }] : []),
              { key: 3, label: 'Contacts' },
              { key: 5, label: 'Webhooks' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={step === tab.key}
                onClick={() => setStep(tab.key)}
                style={navLinkStyle(tab.key)}
              >
                {tab.label}
                {completedSteps.includes(tab.key) && step !== tab.key && (
                  <FaCheckCircle
                    style={{
                      color: 'rgb(4, 120, 87)',
                      fontSize: '0.85rem',
                      marginLeft: '0.25rem',
                      opacity: 0.85,
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-6">{renderStep()}</CardContent>
      </Card>
    </FillPage>
  );
};

export default CampaignDetail;
